import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Settings } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { fmtErr, openExternal } from "@/lib/api";

export function TosAgreement({ agreed, setAgreed, testId = "tos-agree", note = "Membership renews monthly and can be cancelled anytime via the office." }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer" data-testid={testId}>
      <input
        type="checkbox"
        checked={agreed}
        onChange={(e) => setAgreed(e.target.checked)}
        className="w-4 h-4 mt-1 accent-[#C8102E] shrink-0"
        data-testid={`${testId}-checkbox`}
      />
      <span className="text-sm text-white/70 leading-relaxed">
        I agree to the <Link to="/terms" className="underline hover:text-white transition-colors">Terms of Service</Link> and{" "}
        <Link to="/refund" className="underline hover:text-white transition-colors">Refund Policy</Link>. {note}
      </span>
    </label>
  );
}

export default function TalentPassPanel() {
  const { user } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const plan = user?.plan;
  const trialActive = plan?.trial && new Date(plan.trial_ends) > new Date();
  const active = plan?.key === "plan_talent" || trialActive;

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/subscribe", {
        lookup_key: "plan_talent",
        origin_url: window.location.origin,
        agreed,
      });
      openExternal(data.checkout_url);
    } catch (e) {
      toast.error(fmtErr(e));
      setBusy(false);
    }
  };

  const manage = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      openExternal(data.portal_url);
    } catch (e) {
      toast.error(fmtErr(e));
      setBusy(false);
    }
  };

  if (active) {
    const isTrial = plan?.key === "trial";
    return (
      <div className="panel p-8" data-testid="talent-pass-active">
        <p className="overline-label !font-semibold !text-accent mb-3">
          {isTrial ? "Trial Access — Active" : "Talent Pass — Active"}
        </p>
        <p className="text-sm text-muted-foreground leading-relaxed mb-8">
          {isTrial
            ? `Full access courtesy of the office until ${new Date(plan.trial_ends).toLocaleDateString("en-US", { month: "long", day: "numeric" })} — every open call is yours.`
            : "Unlimited applications to every open call, $29.99/month. Renews monthly — cancel anytime via the office."}
        </p>
        {plan?.stripe_subscription_id && (
          <button data-testid="manage-membership-button" onClick={manage} disabled={busy} className="btn-outline w-full disabled:opacity-40">
            <Settings size={14} /> {busy ? "Opening…" : "Manage membership"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="panel p-8" data-testid="talent-pass-panel">
      <p className="overline-label !font-semibold !text-white/85 mb-3">Talent Pass</p>
      <p className="mb-1">
        <span className="font-display text-5xl">$29.99</span>
        <span className="font-grotesk text-xs uppercase tracking-[0.15em] text-muted-foreground"> / month</span>
      </p>
      <p className="font-grotesk text-xs uppercase tracking-[0.15em] text-white/60 mb-6">Apply to every open call · cancel via the office</p>
      <ul className="flex flex-col gap-2 mb-8 text-sm text-white/70">
        <li className="flex items-center gap-2"><Check size={14} className="text-accent" /> Unlimited applications while active</li>
        <li className="flex items-center gap-2"><Check size={14} className="text-accent" /> Shortlist alerts by email</li>
        <li className="flex items-center gap-2"><Check size={14} className="text-accent" /> Cancel via the office, keep access until the period ends</li>
      </ul>
      <TosAgreement agreed={agreed} setAgreed={setAgreed} testId="talent-tos" />
      <button
        data-testid="talent-subscribe-button"
        onClick={subscribe}
        disabled={busy || !agreed}
        className="btn-primary w-full mt-8 disabled:opacity-40"
      >
        {busy ? "Redirecting…" : "Start membership — $29.99/mo"}
      </button>
    </div>
  );
}
